'use client'

import { i18n } from '@quadralize/i18n'
import { cn } from '@quadralize/utils'
import { AltArrowDown, CheckCircle, Magnifier } from '@solar-icons/react'
import { Plus, X } from 'lucide-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useMobile } from '../../../hooks/use-mobile'
import { Badge } from '../../data-display/badge/badge'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../overlays/command/command'
import { Popover, PopoverContent, PopoverTrigger } from '../../overlays/popover/popover'
import { buttonVariants } from '../../ui/button/button.variants'
import { LabelWrapper } from '../label/label'
import type { ComboboxOption, ComboboxProps } from './combobox.types'
import { getComboboxOptionKeywords } from './combobox-search'

const MOBILE_POPOVER_SIDE_OFFSET = 8
const DESKTOP_POPOVER_SIDE_OFFSET = 4

export function Combobox({
  options: propOptions = [],
  fetcher,
  debounceTime = 500,
  value,
  initialLabel = '',
  onValueChange,
  placeholder = 'Selecionar...',
  searchPlaceholder = 'Buscar...',
  emptyMessage = 'Nenhum resultado encontrado.',
  clearable = false,
  className,
  disabled = false,
  label,
  id,
  required,
  error,
  helperText,
  creatable = false,
  onCreate,
  createLabel,
  multiple = false,
  maxCount,
}: ComboboxProps) {
  const { t } = useTranslation('ui', { i18n })
  const generatedId = React.useId()
  const comboboxId = id || generatedId
  const [open, setOpen] = React.useState(false)
  const isMobile = useMobile()

  // State for Async Mode
  const [options, setOptions] = React.useState<ComboboxOption[]>(propOptions)
  const [query, setQuery] = React.useState('')
  const [loading, setLoading] = React.useState(false)
  const [selectedLabel, setSelectedLabel] = React.useState(initialLabel)

  const isAsync = !!fetcher

  // Sync state with props
  React.useEffect(() => {
    if (!isAsync) {
      setOptions(propOptions)
    }
  }, [propOptions, isAsync])

  // Sync initial label if initialLabel prop changes
  React.useEffect(() => {
    setSelectedLabel(initialLabel || '')
  }, [initialLabel])

  // Determine if there is a value
  const hasValue = React.useMemo(() => {
    if (multiple) return Array.isArray(value) && value.length > 0
    return !!value
  }, [value, multiple])
  const hasDisplayValue = React.useMemo(() => {
    if (multiple) return hasValue
    return hasValue || (isAsync && !!selectedLabel)
  }, [hasValue, isAsync, multiple, selectedLabel])

  // Determine the label to display in the trigger
  const currentLabel = React.useMemo(() => {
    if (multiple) return ''

    const findInOptions = () => {
      const found = options.find((opt) => opt.value === (value as string))
      if (found) return found.label
      if (creatable && value) return value as string
      return ''
    }

    if (!isAsync) return findInOptions()

    // In async mode, prioritize selectedLabel but fallback to finding in current options
    return selectedLabel || findInOptions()
  }, [value, options, isAsync, selectedLabel, multiple, creatable])

  const resolvedPlaceholder = placeholder ?? t('combobox.placeholder')
  const resolvedSearchPlaceholder = searchPlaceholder ?? t('combobox.searchPlaceholder')
  const resolvedEmptyMessage = emptyMessage ?? t('combobox.emptyMessage')
  const displayLabel = hasDisplayValue ? currentLabel : resolvedPlaceholder

  // Async Fetch Logic
  React.useEffect(() => {
    if (!isAsync || !fetcher) return

    const loadOptions = async () => {
      setLoading(true)
      try {
        const results = await fetcher(query)
        setOptions(results)
      } catch (error) {
        console.error('Combobox fetch error:', error)
      } finally {
        setLoading(false)
      }
    }

    const timer = setTimeout(loadOptions, debounceTime)
    return () => clearTimeout(timer)
  }, [query, fetcher, isAsync, debounceTime])

  const handleSelect = (val: string, lbl: string) => {
    if (multiple) {
      const currentValues = Array.isArray(value) ? value : []
      const newValue = currentValues.includes(val)
        ? currentValues.filter((v) => v !== val)
        : [...currentValues, val]

      const changeFn = onValueChange as (value: string[]) => void
      changeFn?.(newValue)
    } else {
      const changeFn = onValueChange as (value: string) => void
      changeFn?.(val)
      if (isAsync) setSelectedLabel(lbl)
      setOpen(false)
    }
  }

  const handleClear = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (multiple) {
      const changeFn = onValueChange as (value: string[]) => void
      changeFn?.([])
    } else {
      const changeFn = onValueChange as (value: string) => void
      changeFn?.('')
    }
    if (isAsync) setSelectedLabel('')
  }

  const handleCreate = () => {
    const newValue = query.trim()
    if (!newValue) return
    const shouldSelect = onCreate?.(newValue) !== false
    setQuery('')

    if (!shouldSelect) {
      setOpen(false)
      return
    }

    if (multiple) {
      const currentValues = Array.isArray(value) ? value : []
      const changeFn = onValueChange as (value: string[]) => void
      changeFn?.([...currentValues, newValue])
    } else {
      const changeFn = onValueChange as (value: string) => void
      changeFn?.(newValue)
      if (isAsync) setSelectedLabel(newValue)
      setOpen(false)
    }
  }

  return (
    <LabelWrapper
      label={label}
      id={comboboxId}
      required={required}
      error={error}
      helperText={helperText}
    >
      <div className="relative w-full">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <div
              id={comboboxId}
              role="combobox"
              aria-expanded={open}
              aria-haspopup="listbox"
              aria-controls={`${comboboxId}-list`}
              tabIndex={disabled ? -1 : 0}
              className={cn(
                buttonVariants({ variant: 'outline', size: 'md' }),
                'w-full justify-between hover:bg-transparent border-input-border bg-input-bg text-text-primary ring-offset-bg-base focus:ring-1 focus:ring-focus-ring hover:border-input-border-hover cursor-pointer px-3 transition-all',
                multiple && hasValue
                  ? 'h-auto! md:h-auto! min-h-12 md:min-h-12 items-start py-2 overflow-visible'
                  : 'h-auto min-h-12 md:min-h-11 py-1.5 overflow-hidden',
                error && 'border-error focus:ring-error',
                disabled && 'opacity-50 pointer-events-none',
                className,
              )}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  setOpen((o) => !o)
                }
              }}
            >
              <span
                className={cn(
                  'flex flex-wrap gap-1 flex-1 text-left min-w-0 font-normal',
                  !multiple && 'truncate',
                  !hasDisplayValue && 'text-text-tertiary',
                )}
              >
                {multiple && Array.isArray(value) && value.length > 0 ? (
                  <div className="flex flex-wrap gap-1 py-0.5">
                    {(maxCount ? value.slice(0, maxCount) : value).map((val) => {
                      const optionLabel =
                        isAsync && val === value[value.length - 1] && selectedLabel
                          ? selectedLabel // Rough approximation for async
                          : options.find((opt) => opt.value === val)?.label || val
                      return (
                        <Badge
                          key={val}
                          variant="secondary"
                          className="gap-1.5 py-0 h-7" // Adjusted height and gap
                        >
                          <span className="truncate max-w-30">{optionLabel}</span>
                          <button
                            type="button"
                            tabIndex={-1}
                            className="inline-flex h-4 w-4 items-center justify-center ring-offset-background rounded-full outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 shrink-0"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleSelect(val, '')
                              }
                            }}
                            onMouseDown={(e) => {
                              e.preventDefault()
                              e.stopPropagation()
                            }}
                            onClick={(e) => {
                              e.preventDefault()
                              e.stopPropagation()
                              handleSelect(val, '')
                            }}
                          >
                            <X
                              className="h-3.5 w-3.5 opacity-60 hover:opacity-100 transition-opacity"
                              strokeWidth={3}
                            />
                          </button>
                        </Badge>
                      )
                    })}
                    {maxCount && value.length > maxCount && (
                      <Badge
                        variant="outline"
                        className="bg-background text-text-secondary border-dashed h-7"
                      >
                        +{value.length - maxCount}
                      </Badge>
                    )}
                  </div>
                ) : (
                  <span>{displayLabel}</span>
                )}
              </span>
              <div
                className={cn(
                  'flex items-center shrink-0 ml-2',
                  multiple && hasValue && 'self-center',
                )}
              >
                {clearable && hasDisplayValue ? (
                  <button
                    type="button"
                    tabIndex={-1}
                    onClick={handleClear}
                    onPointerDown={(e) => e.stopPropagation()}
                    className="grid h-5 w-5 place-items-center text-text-tertiary hover:text-text-primary focus:outline-none transition-colors cursor-pointer"
                  >
                    <X className="h-4 w-4 opacity-70 hover:opacity-100" strokeWidth={2.5} />
                  </button>
                ) : (
                  (!multiple || !hasDisplayValue) && <AltArrowDown className="h-4 w-4 opacity-50" />
                )}
              </div>
            </div>
          </PopoverTrigger>
          <PopoverContent
            className="min-w-(--radix-popover-trigger-width) w-auto p-0"
            align="start"
            side={isMobile ? 'top' : 'bottom'}
            sideOffset={isMobile ? MOBILE_POPOVER_SIDE_OFFSET : DESKTOP_POPOVER_SIDE_OFFSET}
            portalled={isMobile}
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            <Command shouldFilter={!isAsync}>
              {/* Note: In Sync mode, we rely on Command's internal filtering (shouldFilter=true). 
                  In Async mode, we disable it (shouldFilter=false) because the fetcher handles filtering on the "server". */}
              <CommandInput
                placeholder={resolvedSearchPlaceholder}
                autoFocus
                value={query}
                onValueChange={setQuery}
              />
              <CommandList>
                {loading ? (
                  <div className="flex items-center justify-center p-4 py-6">
                    <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary-default border-t-transparent" />
                  </div>
                ) : (
                  <>
                    {/* Only show Empty if not loading */}
                    {!loading && options.length === 0 && (
                      <CommandEmpty>
                        <div className="flex flex-col items-center justify-center px-8 gap-2">
                          <Magnifier className="h-6 w-6 text-text-tertiary opacity-40" />
                          <p className="text-sm font-medium text-text-secondary">
                            {resolvedEmptyMessage}
                          </p>
                        </div>
                      </CommandEmpty>
                    )}
                    <CommandGroup>
                      {options.map((option) => (
                        <CommandItem
                          key={option.value}
                          value={option.value}
                          keywords={getComboboxOptionKeywords(option)}
                          onSelect={() => handleSelect(option.value, option.label)}
                        >
                          <CheckCircle
                            className={cn(
                              'mr-2 h-4 w-4',
                              multiple
                                ? Array.isArray(value) && value.includes(option.value)
                                  ? 'opacity-100'
                                  : 'opacity-0'
                                : value === option.value
                                  ? 'opacity-100'
                                  : 'opacity-0',
                            )}
                          />
                          {option.label}
                        </CommandItem>
                      ))}
                      {creatable &&
                        query &&
                        !options.some((opt) => opt.label.toLowerCase() === query.toLowerCase()) && (
                          <CommandItem
                            value={`create:${query}`}
                            onSelect={handleCreate}
                            className="text-primary cursor-pointer"
                          >
                            <Plus className="mr-2 h-4 w-4" strokeWidth={2.5} />
                            {createLabel ? createLabel(query) : `Criar "${query}"`}
                          </CommandItem>
                        )}
                    </CommandGroup>
                  </>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
    </LabelWrapper>
  )
}
